<template>
  <v-container class="general-panel pa-0" fluid>
    <loading-box :loading="true" v-if="!machine" />

    <div v-else class="mt-4 pb-3">
      <!-- Product Information Section -->
      <div class="section-header d-flex align-center mb-3">
        <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
        <h3 class="text-h6 font-weight-medium ma-0">
          {{ $t("text.general") }}
        </h3>
      </div>

      <v-card variant="outlined" class="mb-5 rounded-lg">
        <v-card-text class="pa-0">
          <v-row no-gutters>
            <!-- Machine Image -->
            <v-col cols="auto" class="d-none d-sm-flex">
              <MachineModelImage :size="140" :relativePath="machineModelImage" />
            </v-col>

            <!-- Product Details -->
            <v-col>
              <div class="pa-4">
                <div class="text-subtitle-1 font-weight-bold mb-3" style="color: rgba(0,0,0,.87);">
                  {{ machine.DisplayName || machine.Name || '—' }}
                </div>
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-label">{{ $t("text.type") }}</div>
                    <div class="detail-value">{{ machineTypeName }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-label">{{ $t("text.supplier") }}</div>
                    <div class="detail-value">{{ supplierName }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-label">{{ $t("text.model") }}</div>
                    <div class="detail-value">{{ machineModelName }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-label">{{ $t("text.serial_number") }}</div>
                    <div class="detail-value primary--text text--darken-3">{{ machine.SerialNumber || '—' }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-label">{{ $t("text.facility") }}</div>
                    <div class="detail-value">{{ machine.Facility }}</div>
                  </v-col>
                  <v-col v-if="machine.Qrcode" cols="12" sm="6" md="4">
                    <div class="detail-label">{{ $t("text.qr_code") }}</div>
                    <div class="detail-value primary--text text--darken-3">{{ machine.Qrcode }}</div>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Important Dates Section -->
      <div class="section-header d-flex align-center mb-3">
        <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
        <h3 class="text-h6 font-weight-medium ma-0">
          {{ $t("text.date") }}
        </h3>
      </div>

      <v-card variant="outlined" class="mb-5 rounded-lg">
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="detail-label">
                <v-icon x-small class="mr-1" color="grey">mdi-cart-outline</v-icon>
                {{ $t("text.purchase_date") }}
              </div>
              <div class="detail-value" v-if="machine.BoughtDate">{{ formatDate(machine.BoughtDate) }}</div>
              <div class="detail-value text--disabled" v-else>—</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="detail-label">
                <v-icon x-small class="mr-1" color="grey">mdi-wrench</v-icon>
                {{ $t("text.installation_date") }}
              </div>
              <div class="detail-value">{{ formatDate(machine.InstallationDate) }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="detail-label">
                <v-icon x-small class="mr-1" color="grey">mdi-shield-check-outline</v-icon>
                {{ $t("text.warrenty_expires") }}
              </div>
              <div class="detail-value d-flex flex-column align-start">
                {{ formatDate(machine.WarrantyExpiresDate) }}
                <v-chip v-if="warrantyActive || machine.WarrantyExpiresDate" x-small
                  :color="warrantyActive ? 'success' : 'grey'" dark>
                  <v-icon size="x-small" class="mr-1">
                    {{ warrantyActive ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  {{ warrantyActive ? $t("text.active") : $t("text.expired") }}
                </v-chip>
              </div>
            </v-col>
            <v-col v-if="machine.RetiredDate" cols="12" sm="6" md="3">
              <div class="detail-label">
                <v-icon x-small class="mr-1" color="grey">mdi-archive-outline</v-icon>
                {{ $t("text.retired") }}
              </div>
              <div class="detail-value">{{ formatDate(machine.RetiredDate) }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Status Section -->
      <div class="section-header d-flex align-center mb-3">
        <v-icon color="primary" class="mr-2">mdi-toggle-switch-outline</v-icon>
        <h3 class="text-h6 font-weight-medium ma-0">
          {{ $t("text.status") }}
        </h3>
      </div>

      <v-card variant="outlined" class="mb-5 rounded-lg">
        <v-card-text class="pa-4">
          <v-row align="center">
            <v-col cols="auto">
              <v-chip :color="machine.IsActive ? 'success' : 'grey'" dark small class="font-weight-medium">
                <v-icon size="small" class="mr-1">{{ machine.IsActive ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                {{ machine.IsActive ? $t("text.active") : $t("text.inactive") }}
              </v-chip>
            </v-col>
            <v-col cols="auto">
              <v-chip :color="machine.ServiceAgreement ? 'primary' : 'grey lighten-1'" :dark="machine.ServiceAgreement"
                small class="font-weight-medium">
                <v-icon size="small" class="mr-1">{{ machine.ServiceAgreement ? 'mdi-file-document-check-outline' :
                  'mdi-file-document-remove-outline' }}</v-icon>
                {{ $t("text.service_agreement") }}
              </v-chip>
            </v-col>
          </v-row>

          <!-- Note -->
          <div v-if="machine.Note" class="mt-4">
            <div class="detail-label">
              <v-icon x-small class="mr-1" color="grey">mdi-note-text-outline</v-icon>
              {{ $t("text.comment") }}
            </div>
            <div class="detail-value text-body-2" style="white-space: pre-wrap;">{{ machine.Note }}</div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Actions -->
      <div v-if="editmode" class="d-flex justify-end mb-4">
        <v-btn text class="mr-2" @click="editmode = false">
          {{ $t("text.cancel") }}
        </v-btn>
        <v-btn v-if="dirty" color="primary" depressed @click="editmode = false">
          <v-icon left small>mdi-content-save</v-icon>
          {{ $t("text.save") }}
        </v-btn>
      </div>
    </div>
  </v-container>
</template>


<script>
import LoadingBox from "@/components/LoadingBox.vue";
import MachineModelImage from "@/components/Cards/MachineModelImage.vue";

export default {
  name: "ViewMachine",
  components: {
    "loading-box": LoadingBox,
    MachineModelImage,
  },

  props: {
    machine: { type: Object, default: null },
  },

  data() {
    return {
      dirty: false,
      editmode: false,
    };
  },

  mounted() {
    if (!this.machine) this.$emit("updateMachine");
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
  },

  computed: {
    machineModelImage() {
      return this.machine && this.machine.MachineModel
        ? this.machine.MachineModel.ModelImage
        : null;
    },
    machineTypeName() {
      if (this.machine && this.machine.MachineType) {
        return this.machine.MachineType.Name;
      }
      if (this.machine && this.machine.MachineModel && this.machine.MachineModel.MachineType) {
        return this.machine.MachineModel.MachineType.Name;
      }
      return '—';
    },
    machineModelName() {
      if (this.machine && this.machine.MachineModel) {
        return this.machine.MachineModel.Name;
      }
      return '—';
    },
    supplierName() {
      if (this.machine && this.machine.Supplier) {
        return this.machine.Supplier.Name;
      }
      return '—';
    },
    warrantyActive() {
      if (!this.machine || !this.machine.WarrantyExpiresDate) return false;
      return new Date(this.machine.WarrantyExpiresDate) > new Date();
    },
  },
};
</script>

<style lang="scss" scoped>
.general-panel {
  max-width: 900px;

  .section-header {
    padding: 0 4px;
  }

  .detail-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 2px;
    font-weight: 500;
  }

  .detail-value {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.87);
    min-height: 24px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
}
</style>